import { ActionCreatorsMapObject, Dispatch, ActionCreator } from 'redux';
import { ThunkAction } from 'redux-thunk';
import axios from 'axios';
import { IUserState, IUserAction } from '../../common/model/UserModels';
import { showNotification } from '../Notifications/NotificationActions';
import { sec } from '../../security';
import config from '../../auth.config';

const API_ROOT = process.env.REACT_APP_API_ROOT;

export const types = {
    GET_USER_REQUEST: 'GET_USER_REQUEST',
    GET_USER_SUCCESS: 'GET_USER_SUCCESS',
    GET_USER_FAILURE: 'GET_USER_FAILURE',
    UPDATE_USER_REQUEST: 'UPDATE_USER_REQUEST',
    UPDATE_USER_SUCCESS: 'UPDATE_USER_SUCCESS',
    UPDATE_USER_FAILURE: 'UPDATE_USER_FAILURE',
    UPDATE_USER_CONFIG_REQUEST: 'UPDATE_USER_CONFIG_REQUEST',
    UPDATE_USER_CONFIG_SUCCESS: 'UPDATE_USER_CONFIG_SUCCESS',
    UPDATE_USER_CONFIG_FAILURE: 'UPDATE_USER_CONFIG_FAILURE',
    GET_USER_CONFIG_REQUEST: 'GET_USER_REQUEST',
    GET_USER_CONFIG_SUCCESS: 'GET_USER_SUCCESS',
    GET_USER_CONFIG_FAILURE: 'GET_USER_FAILURE',
    GET_PICTURE_REQUEST: 'GET_PICTURE_REQUEST',
    GET_PICTURE_SUCCESS: 'GET_PICTURE_SUCCESS',
    GET_PICTURE_FAILURE: 'GET_PICTURE_FAILURE',
    UPDATE_PICTURE_REQUEST: 'UPDATE_PICTURE_REQUEST',
    UPDATE_PICTURE_SUCCESS: 'UPDATE_PICTURE_SUCCESS',
    UPDATE_PICTURE_FAILURE: 'UPDATE_PICTURE_FAILURE',
};

const updateUser: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = (data: any) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });

    dispatch({
        type: types.UPDATE_USER_REQUEST,
    });

    try {
        const response = await axios({
            method: 'PUT',
            url: `${API_ROOT}/user/`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data,
        });

        dispatch({
            type: types.UPDATE_USER_SUCCESS,
            payload: {
                userData: response.data,
                error: undefined,
            },
        });
        dispatch(
            showNotification(
                'Your information was saved successfully',
                'success'
            )
        );
    } catch (error) {
        console.log(
            'Fetch User Error',
            error instanceof Error ? error.message : 'Unknown error'
        );
        dispatch(
            showNotification(
                'There was an issue updating your profile. Please try again',
                'error'
            )
        );
        dispatch({
            type: types.UPDATE_USER_FAILURE,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

const updateUserConfig: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = (data: any) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });

    dispatch({
        type: types.UPDATE_USER_CONFIG_REQUEST,
    });

    try {
        const response = await axios({
            method: 'PUT',
            url: `${API_ROOT}/user/config/`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
            data,
        });

        dispatch({
            type: types.UPDATE_USER_CONFIG_SUCCESS,
            payload: {
                userData: response.data,
                error: undefined,
            },
        });
        dispatch(
            showNotification(
                'Your information was saved successfully',
                'success'
            )
        );
    } catch (error) {
        console.log(
            'Fetch User Error',
            error instanceof Error ? error.message : 'Unknown error'
        );
        dispatch(
            showNotification(
                'The information you entered was invalid. Please try again',
                'error'
            )
        );
        dispatch({
            type: types.UPDATE_USER_CONFIG_FAILURE,
            error: error instanceof Error ? error.message : 'Unknown error',
        });
    }
};

const getUser: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = () => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });

    dispatch({
        type: types.GET_USER_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/user/`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.GET_USER_SUCCESS,
            payload: {
                userData: response.data,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Fetch User Error', error);
        dispatch({
            type: types.GET_USER_FAILURE,
            error: 'There was an issue fetching the user data. Please try again later.',
        });
    }
};

const getUserConfig: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = () => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });

    dispatch({
        type: types.GET_USER_CONFIG_REQUEST,
    });

    try {
        const response = await axios({
            method: 'GET',
            url: `${API_ROOT}/user/config/`,
            headers: {
                'Access-Control-Allow-Origin': '*',
                'Content-Type': 'application/json;charset=UTF-8',
                Accept: 'application/json',
                Authorization: 'Bearer ' + access_token,
            },
        });

        dispatch({
            type: types.GET_USER_CONFIG_SUCCESS,
            payload: {
                userData: response.data,
                error: undefined,
            },
        });
    } catch (error) {
        console.log('Fetch User Error', error);
        dispatch({
            type: types.GET_USER_CONFIG_FAILURE,
            error: 'There was an issue fetching the user data. Please try again later.',
        });
    }
};

const getPicture: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = () => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });

    // Set it loading
    dispatch({
        type: types.GET_PICTURE_REQUEST,
    });
    // Make the request
    return axios({
        method: 'GET',
        url: `${API_ROOT}/user/pic`,
        headers: {
            'Content-Type': 'multipart/mixed',
            Accept: 'application/json',
            Authorization: 'Bearer ' + access_token,
        },
        responseType: 'blob',
    })
        .then((response) => {
            let url = URL.createObjectURL(response.data);
            dispatch({
                type: types.GET_PICTURE_SUCCESS,
                payload: url,
            });
        })
        .catch((error) => {
            if (error.response) {
                console.log(error.response.data);
            }
        });
};

// action to update the profile picture. When action for profile picture update is performed relevant message will be
// displayed in snackbar on screen
const updatePicture: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = (file: File) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });
    const formData: FormData = new FormData();
    formData.append('image', file);
    let url = URL.createObjectURL(file);

    // Set it loading
    dispatch({
        type: types.UPDATE_PICTURE_REQUEST,
    });

    // Make the request
    return axios({
        method: 'POST',
        url: `${API_ROOT}/user/pic`,
        data: formData,
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
            Authorization: 'Bearer ' + access_token,
        },
    })
        .then((response) => {
            dispatch({
                type: types.GET_PICTURE_SUCCESS,
                payload: url,
            });
            dispatch(
                showNotification(
                    'Profile Picture Successfully Updated',
                    'success'
                )
            );
        })
        .catch((error) => {
            if (error.response) {
                console.log(error.response.data);
                dispatch(
                    showNotification(
                        'Something went wrong. Please try again',
                        'error'
                    )
                );
            }
        });
};

const deletePicture: ActionCreator<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = (file: File) => async (dispatch: Dispatch) => {
    const access_token = await sec.getAccessTokenSilently()({
        scope: config.scope,
        audience: config.audience,
    });
    const formData: FormData = new FormData();
    formData.append('image', file);

    // Set it loading
    dispatch({
        type: types.UPDATE_PICTURE_REQUEST,
    });

    // Make the request
    return axios({
        method: 'POST',
        url: `${API_ROOT}/user/pic`,
        data: formData,
        headers: {
            'Access-Control-Allow-Origin': '*',
            'Content-Type': 'multipart/form-data',
            Accept: 'application/json',
            Authorization: 'Bearer ' + access_token,
        },
    })
        .then((response) => {
            dispatch({
                type: types.GET_PICTURE_SUCCESS,
                payload: null,
            });
        })
        .catch((error) => {
            if (error.response) {
                console.log(error.response.data);
            }
        });
};

export const userActions: ActionCreatorsMapObject<
    ThunkAction<Promise<any>, IUserState, null, IUserAction>
> = {
    getUser,
    updateUser,
    getUserConfig,
    updateUserConfig,
    getPicture,
    updatePicture,
    deletePicture,
};
